import { UpdateInspectionContent } from "interfaces/models/inspectionContent";
import React, {
  createContext,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
} from "react";
import { sort } from "../../helpers/func";
import useSaveInspectionContent from "../../hooks/useSaveInspectionContent";
import { initInspectionData } from "./actions";
import { formBuilderReducer } from "./reducer";
import {
  FormBuilderActions,
  FormBuilderContextType,
  FormBuilderState,
} from "./types";

export const FormBuilderContext = createContext<FormBuilderContextType>(
  {} as any
);

export interface FormBuilderContextRef {
  isDirty: boolean;
  dispatch(action: FormBuilderActions): void;
  validate: () => Promise<any>;
  save: () => Promise<UpdateInspectionContent>;
}

export const useFormBuilder = () => {
  return useContext(FormBuilderContext);
};

export interface FormBuilderContextProviderProps {
  children?: any;
  defaultValue?: FormBuilderState;
}

const initState: FormBuilderState = {
  contentItems: [],
  itemResults: [],
  inspectionContent: {} as any,
  mapRowOrderInfo: {},
};

export const FormBuilderContextProvider = React.forwardRef<
  FormBuilderContextRef,
  FormBuilderContextProviderProps
>(({ children, defaultValue }, ref) => {
  const [state, dispatch] = useReducer(formBuilderReducer, initState);

  const { isDirty, isSavingContent, errors, validate, onSaveContent } =
    useSaveInspectionContent({
      state,
      dispatch,
    });

  const mapRowOrderInfo = useMemo(() => {
    const contentItems = sort(state.contentItems);
    const allItemResultCount = state.itemResults.length;
    const rowOrderInfo: NonNullable<FormBuilderState["mapRowOrderInfo"]> = {};

    contentItems.forEach((item, index) => {
      const prevItemResultCount = state.itemResults?.filter((i) => {
        const ids = contentItems.filter(
          (contentItem, idx) =>
            idx < index && contentItem.id === i.contentItemId
        );

        return ids.length;
      })?.length;

      const itemResults = sort(
        state.itemResults.filter((result) => result.contentItemId === item.id)
      );

      itemResults.forEach((row, rowIndex) => {
        rowOrderInfo[row.id] = {
          index: prevItemResultCount + rowIndex + 1,
          total: allItemResultCount,
        };
      });
    });

    return rowOrderInfo;
  }, [state.contentItems, state.itemResults]);

  useImperativeHandle(
    ref,
    () => ({
      dispatch,
      validate,
      isDirty,
      save: onSaveContent,
    }),
    [isDirty, validate, onSaveContent]
  );

  useEffect(() => {
    if (defaultValue) {
      dispatch(initInspectionData(Object.assign(initState, defaultValue)));
    }
  }, [defaultValue]);

  const value = useMemo(() => {
    return {
      isSavingContent,
      state: { ...state, mapRowOrderInfo },
      errors,
      dispatch,
      onSaveContent,
    };
  }, [
    isSavingContent,
    state,
    mapRowOrderInfo,
    errors,
    dispatch,
    onSaveContent,
  ]);

  return (
    <FormBuilderContext.Provider value={value}>
      {children}
    </FormBuilderContext.Provider>
  );
});
